import React from 'react';
import { Result, Spin } from 'antd';
import { useAssetMasterAccess } from '../context/AssetMasterAccessContext';

export type AssetMasterPermission =
  | 'read'
  | 'assetmaster'
  | 'write'
  | 'custodian'
  | 'approver';

interface AssetMasterRouteGuardProps {
  children: React.ReactNode;
  /**
   * Required permissions for this route.
   * - 'read': Assets / Delivery Status pages — canRead OR isCustodian OR canWrite
   * - 'assetmaster': Asset Master list page — canAssetMaster OR canWrite
   * - 'write': Categories / Approval pages — canWrite
   * - 'custodian': legacy alias, canWrite OR isCustodian OR canRead
   * - 'approver': approval action — isApprover
   */
  requiredPermission: AssetMasterPermission;
}

/**
 * Route guard component for Asset Master pages.
 * Checks specific permissions based on the route requirements.
 */
const AssetMasterRouteGuard: React.FC<AssetMasterRouteGuardProps> = ({
  children,
  requiredPermission,
}) => {
  const {
    loading,
    canRead,
    canAssetMaster,
    canWrite,
    isCustodian,
    isApprover,
  } = useAssetMasterAccess();

  // Show loading spinner while permissions are being fetched
  if (loading) {
    return (
      <div
        style={{
          minHeight: 'calc(100vh - 52px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // Check permissions based on the required permission type
  let hasPermission = false;

  switch (requiredPermission) {
    case 'read':
      hasPermission = canRead || isCustodian || canWrite;
      break;
    case 'assetmaster':
      hasPermission = canAssetMaster || canWrite;
      break;
    case 'write':
      hasPermission = canWrite;
      break;
    case 'custodian':
      hasPermission = canWrite || isCustodian || canRead;
      break;
    case 'approver':
      hasPermission = isApprover;
      break;
    default:
      hasPermission = false;
  }

  // If user doesn't have permission, show access denied
  if (!hasPermission) {
    return (
      <Result
        status="403"
        title="Access Denied"
        subTitle={getAccessDeniedMessage(requiredPermission)}
        extra={
          canAssetMaster || canWrite ? (
            <a href="/assetmaster/master">Go to Asset Master</a>
          ) : canRead || isCustodian ? (
            <a href="/assetmaster/assets">Go to Assets</a>
          ) : (
            <a href="/project-selection">Go Back</a>
          )
        }
      />
    );
  }

  return <>{children}</>;
};

/**
 * Get a user-friendly message for access denied based on permission type
 */
function getAccessDeniedMessage(permission: AssetMasterPermission): string {
  switch (permission) {
    case 'read':
      return 'You do not have permission to view this page.';
    case 'assetmaster':
      return 'You do not have Asset Master access. Contact your administrator for access.';
    case 'write':
      return 'You do not have write access to this page. Contact your administrator for access.';
    case 'custodian':
      return 'This page is restricted to custodians or users with write access.';
    case 'approver':
      return 'This page is restricted to approvers only.';
    default:
      return 'You do not have permission to access this page.';
  }
}

export default AssetMasterRouteGuard;
